import React, { useState, useEffect } from 'react';
import { FiDownload } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import ProfileSidebar from '../../components/ProfileSidebar/ProfileSidebar';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import TwoFactorAuthModal from '../../../components/TwoFactorAuthModal/TwoFactorAuthModal';
import SearchableSelect from '../../../components/SearchableSelect/SearchableSelect';
import { useProfile } from '../../hooks';
import { useForm } from '../../hooks';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { updateUserProfileImage, logoutUser } from '../../../store/slices/authSlice';
import { showSuccessToast, showErrorToast } from '../../../utils/messageHelper';
import utilityService from '../../../services/utilityService';
import api from '../../../services/api';

const UserDetailsPage = () => {
  const dispatch = useAppDispatch();
  const { profile, twoFactorEnabled, loading, loadProfile, updateProfile, enableTwoFactor, disableTwoFactor } = useProfile();
  const { profileLoading } = useAppSelector((state) => state.auth);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [nationalities, setNationalities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingNationalities, setLoadingNationalities] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [deactivating, setDeactivating] = useState(false);


  // Form validation
  const validateForm = (values) => {
    const errors = {};

    if (!values.first_name?.trim()) {
      errors.first_name = 'First name is required';
    }

    if (!values.last_name?.trim()) {
      errors.last_name = 'Last name is required';
    }

    // Email validation
    if (!values.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Validate date of birth - should not be in the future
    if (values.date_of_birth) {
      const selectedDate = new Date(values.date_of_birth);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to compare only dates
      
      if (selectedDate > today) {
        errors.date_of_birth = 'Date of birth cannot be in the future';
      }
    }

    // Mobile number is optional for initial save

    return errors;
  };

  const fetchNationalities = async () => {
    try {
      setLoadingNationalities(true);
      const response = await utilityService.getNationalities();
      if (response.success) {
        setNationalities(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch nationalities:', err);
    } finally {
      setLoadingNationalities(false);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await utilityService.getCountries();
      if (response.success) {
        setCountries(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch countries:', err);
    } finally {
      setLoadingCountries(false);
    }
  };

  const { values, errors, touched, handleChange, handleBlur, validate, reset } = useForm(
    {
      first_name: '',
      last_name: '',
      email: '',
      mobile_number: '',
      country_id: '',
      nationality: '',
      date_of_birth: '',
      address: '',
      business_name: '',
      account_type: '',
      user_type: ''
    },
    validateForm
  );

  useEffect(() => {
    fetchNationalities();
    fetchCountries();
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (profile) {
      // Format date for HTML date input (YYYY-MM-DD)
      let formattedDate = '';
      if (profile.date_of_birth) {
        try {
          const date = new Date(profile.date_of_birth);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().split('T')[0];
          }
        } catch (error) {
          // Error formatting date
        }
      }

      const profileData = {
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        mobile_number: profile.mobile_number || '',
        country_id: profile.country_id || '',
        nationality: profile.nationality || '',
        date_of_birth: formattedDate,
        address: profile.address || '',
        business_name: profile.business_name || '',
        account_type: profile.account_type || '',
        user_type: profile.user_type || '',
      };

      reset(profileData);
      setProfileImage(profile.profileImage || null);
    }
  }, [profile, reset]); // Added reset back since it's now stable

  const handleProfileImageSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleConfirmEdit = () => {
    setIsEditing(true);
    setShowEditModal(false);
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
  };

  const handleSave = async () => {
    const isValid = validate();

    if (!isValid) {
      showErrorToast('general.somethingWentWrong');
      return;
    }

    try {
      // Prepare profile data - only send non-empty values for optional fields
      const profileData = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
      };

      // Add optional fields only if they have values
      if (values.mobile_number && values.mobile_number.trim()) {
        profileData.mobile_number = values.mobile_number.trim();
      }
      if (values.nationality && values.nationality.trim()) {
        profileData.nationality = values.nationality.trim();
      }
      if (values.date_of_birth && values.date_of_birth.trim()) {
        profileData.date_of_birth = values.date_of_birth.trim();
      }
      if (values.address && values.address.trim()) {
        profileData.address = values.address.trim();
      }
      if (values.country_id) {
        profileData.country_id = values.country_id;
      }
      if (values.business_name && values.business_name.trim()) {
        profileData.business_name = values.business_name.trim();
      }

      // Update profile data using existing hook
      await updateProfile(profileData);

      showSuccessToast('profile.profileUpdated');

      // Update profile image if a new one was selected
      if (selectedImageFile) {
        // Convert file to base64
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const base64Image = e.target.result;
            await dispatch(updateUserProfileImage(base64Image)).unwrap();
            showSuccessToast('profile.profileImageUploadSuccess');
            setSelectedImageFile(null);
            // Refetch profile to get updated image_url
            await loadProfile();
          } catch (error) {
            showErrorToast('general.somethingWentWrong');
          }
        };
        reader.readAsDataURL(selectedImageFile);
      }
      setIsEditing(false);
      // Reload profile to get updated data
      await loadProfile();
    } catch (error) {
      showErrorToast('general.somethingWentWrong');
    }
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
    // Reload profile data
    if (profile) {
      Object.keys(values).forEach(key => {
        if (profile[key] !== undefined) {
          handleChange(key, profile[key]);
        }
      });
    }
  };

  const handleTwoFactorToggle = async (e) => {
    const isEnabled = e.target.checked;

    if (isEnabled) {
      setShowTwoFactorModal(true);
    } else {
      // Show confirmation before disabling
      setShowDisable2FAModal(true);
    }
  };

  const handleConfirmDisable2FA = async () => {
    try {
      await disableTwoFactor();
      showSuccessToast('twoFactor.twoFactorSaved');
      setShowDisable2FAModal(false);
      await loadProfile();
    } catch (error) {
      showErrorToast('general.somethingWentWrong');
      setShowDisable2FAModal(false);
    }
  };

  const handleCancelDisable2FA = () => {
    setShowDisable2FAModal(false);
  };

  const handleTwoFactorSave = async (authData) => {
    try {
      await enableTwoFactor({
        method: authData.method,
        security_questions: authData.security_questions
      });
      showSuccessToast('twoFactor.twoFactorCreated');
      setShowTwoFactorModal(false);
      await loadProfile();
    } catch (error) {
      showErrorToast('general.somethingWentWrong');
    }
  };

  const handleTwoFactorClose = () => {
    setShowTwoFactorModal(false);
  };

  const handleDeactivateAccount = () => {
    setShowDeactivateModal(true);
  };

  const handleConfirmDeactivate = async () => {
    try {
      setDeactivating(true);
      
      // Prepare payload with required fields
      const payload = {
        first_name: profile?.first_name || values.first_name,
        last_name: profile?.last_name || values.last_name,
        status: 'Inactive'
      };
      
      const response = await api.put('/auth/profile', payload);

      if (response.success) {
        showSuccessToast(
          'Account deactivated successfully. Please contact admin for reactivation.'
        );
        setShowDeactivateModal(false);

        // Logout user after deactivation
        setTimeout(() => {
          dispatch(logoutUser());
        }, 2000);
      }
    } catch (error) {
      showErrorToast(error.message || 'Failed to deactivate account');
    } finally {
      setDeactivating(false);
    }
  };

  const handleCancelDeactivate = () => {
    setShowDeactivateModal(false);
  };

  const handleExportAccountInfo = () => {
    if (!profile) {
      showErrorToast('No profile data available to export');
      return;
    }

    try {
      // Format date for display
      const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        } catch {
          return dateString;
        }
      };

      // Prepare data for Excel - Personal Information
      const personalInfoData = [
        ['Field', 'Value'],
        ['First Name', profile.first_name || ''],
        ['Last Name', profile.last_name || ''],
        ['Email', profile.email || ''],
        ['Mobile Number', profile.mobile_number || ''],
        ['Date of Birth', formatDate(profile.date_of_birth)],
        ['Address', profile.address || ''],
        ['Nationality', profile.nationality || ''],
      ];

      // Prepare data for Excel - Account Information
      const accountInfoData = [
        ['Field', 'Value'],
        ['Account Type', profile.account_type || ''],
        ['User Type', profile.user_type || ''],
        ['Business Name', profile.business_name || ''],
        ['Two-Factor Authentication', twoFactorEnabled ? 'Enabled' : 'Disabled'],
      ];

      // Prepare data for Excel - Export Information
      const exportInfoData = [
        ['Field', 'Value'],
        ['Export Date', new Date().toLocaleString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        })],
      ];

      // Create workbook and worksheets
      const workbook = XLSX.utils.book_new();
      
      const personalInfoSheet = XLSX.utils.aoa_to_sheet(personalInfoData);
      const accountInfoSheet = XLSX.utils.aoa_to_sheet(accountInfoData);
      const exportInfoSheet = XLSX.utils.aoa_to_sheet(exportInfoData);

      // Set column widths for better readability
      personalInfoSheet['!cols'] = [{ wch: 20 }, { wch: 40 }];
      accountInfoSheet['!cols'] = [{ wch: 25 }, { wch: 30 }];
      exportInfoSheet['!cols'] = [{ wch: 20 }, { wch: 40 }];

      // Add worksheets to workbook
      XLSX.utils.book_append_sheet(workbook, personalInfoSheet, 'Personal Information');
      XLSX.utils.book_append_sheet(workbook, accountInfoSheet, 'Account Information');
      XLSX.utils.book_append_sheet(workbook, exportInfoSheet, 'Export Details');

      // Generate filename
      const filename = `account-info-${profile.email || 'user'}-${new Date().toISOString().split('T')[0]}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, filename);

      showSuccessToast('Account information exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      showErrorToast('Failed to export account information');
    }
  };

  if (loading && !profile) {
    return (
      <div className="bg-jet-gray-100 pt-[80px] md:pt-[90px] pb-8 px-3 sm:px-5">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
            <p className="text-jet-gray-600">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-jet-gray-100 pt-[40px] pb-12 px-3 sm:px-5">
      <div className="bg-white p-[28px] rounded-[10px] max-w-7xl mx-auto flex flex-col lg:flex-row gap-5">
        <ProfileSidebar />

        <div className="flex-1 bg-white rounded-lg shadow-jet overflow-hidden">
          <div className="bg-jet-yellow px-5 py-3 border-b border-yellow-200 rounded-t-[calc(0.25rem-1px)] flex items-center justify-between">
            <h3 className="text-[18px] font-semibold text-[#000] m-0">User Details</h3>
            <button
              onClick={handleExportAccountInfo}
              className="flex items-center gap-2 bg-white text-jet-gray-800 border border-jet-gray-300 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-all duration-300 hover:bg-jet-gray-50 hover:border-jet-gray-400 focus:outline-none focus:shadow-[0_0_0_2px_rgba(0,0,0,0.1)]"
              title="Export Account Information"
            >
              <FiDownload className="text-base" />
              <span className="hidden sm:inline">Export Info</span>
            </button>
          </div>

          <div className="p-3 sm:p-5 pb-8">
            <div className="flex flex-col md:flex-row gap-0">
              {/* Left Column: Avatar + 2FA */}
              <div className="flex flex-col items-center justify-start gap-6 w-full md:w-1/2 p-4 sm:p-8 pt-12 bg-gray-50 border-b md:border-b-0 md:border-r border-jet-gray-200">
                {/* Profile Image Upload - Simple Circle */}
                <div className="profile-image-section w-full max-w-sm flex flex-col items-center">
                  <label htmlFor="profile-image-input" className="cursor-pointer">
                    <div className="w-[200px] h-[200px] rounded-full border-2 border-gray-300 overflow-hidden hover:border-jet-yellow transition-colors bg-gray-100 flex items-center justify-center">
                      {profileImage ? (
                        <img
                          src={profileImage}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : profile?.image_url ? (
                        <img
                          src={profile.image_url}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-center text-gray-400">
                          <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                          </svg>
                          <p className="text-xs">No Image Available</p>
                        </div>
                      )}
                    </div>
                  </label>

                  <input
                    id="profile-image-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleProfileImageSelect(file);
                      }
                    }}
                    className="hidden"
                  />

                  <p className="text-sm text-gray-600 mt-2 text-center">
                    Profile Picture
                  </p>
                </div>

                {/* 2FA Section - Below Avatar */}
                <div className="twofa-container flex items-center justify-between px-2 sm:px-4 py-2 sm:py-4 bg-white border border-jet-gray-200 rounded-xl w-full max-w-80 shadow-sm transition-all duration-300 hover:border-jet-gray-300 hover:shadow-jet-hover mb-8">
                  <span className="twofa-label text-lg font-semibold text-jet-gray-800 tracking-tight">2FA Authentication</span>
                  <div className="toggle-container relative flex items-center">
                    <input
                      type="checkbox"
                      id="twofa-toggle"
                      checked={twoFactorEnabled}
                      onChange={handleTwoFactorToggle}
                      className="toggle-input absolute opacity-0 pointer-events-none w-0 h-0"
                    />
                    <label
                      htmlFor="twofa-toggle"
                      className={`toggle-label block w-12 sm:w-14 h-6 sm:h-7 rounded-full cursor-pointer transition-all duration-300 ease-jet border-2 border-transparent relative outline-none ${twoFactorEnabled
                        ? 'bg-jet-yellow border-jet-yellow-border hover:bg-jet-yellow-hover'
                        : 'bg-jet-gray-200 hover:bg-jet-gray-300'
                        } focus-within:shadow-jet-focus`}
                    >
                      <span className={`toggle-slider block w-4 sm:w-5 h-4 sm:h-5 bg-white rounded-full shadow-jet-toggle transition-all duration-300 ease-jet mt-0.5 border border-black border-opacity-5 ${twoFactorEnabled
                        ? 'transform translate-x-7 sm:translate-x-8 shadow-jet-toggle-active'
                        : 'transform translate-x-0.5'
                        }`}></span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Right Column: Form Fields */}
              <div className="w-full md:w-1/2 flex flex-col p-4 sm:p-8 bg-white pb-8">
                <div className="form-fields flex flex-col gap-4 mb-2">
                  <div className="form-field flex flex-col">
                    <input
                      type="text"
                      className={`profile-input w-full px-4 py-3 border rounded-md text-sm transition-colors duration-300 focus:outline-none ${isEditing ? 'bg-white text-jet-gray-800 focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]' : 'bg-gray-100 text-jet-gray-600'} ${errors.first_name && touched.first_name ? 'border-red-500' : 'border-jet-gray-300'
                        }`}
                      value={values.first_name ? values.first_name.toUpperCase() : ''}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      onBlur={() => handleBlur('first_name')}
                      readOnly={!isEditing}
                      placeholder="FIRST NAME"
                    />
                    {errors.first_name && touched.first_name && (
                      <span className="text-red-500 text-xs mt-1">{errors.first_name}</span>
                    )}
                  </div>
                  <div className="form-field flex flex-col">
                    <input
                      type="text"
                      className={`profile-input w-full px-4 py-3 border rounded-md text-sm transition-colors duration-300 focus:outline-none ${isEditing ? 'bg-white text-jet-gray-800 focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]' : 'bg-gray-100 text-jet-gray-600'} ${errors.last_name && touched.last_name ? 'border-red-500' : 'border-jet-gray-300'
                        }`}
                      value={values.last_name ? values.last_name.toUpperCase() : ''}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      onBlur={() => handleBlur('last_name')}
                      readOnly={!isEditing}
                      placeholder="LAST NAME"
                    />
                    {errors.last_name && touched.last_name && (
                      <span className="text-red-500 text-xs mt-1">{errors.last_name}</span>
                    )}
                  </div>
                  {(values.account_type === 'Business' || values.user_type === 'Airliner') && (
                    <div className="form-field flex flex-col">
                      <input
                        type="text"
                        className={`profile-input w-full px-4 py-3 border border-jet-gray-300 rounded-md text-sm transition-colors duration-300 focus:outline-none ${isEditing ? 'bg-white text-jet-gray-800 focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]' : 'bg-gray-100 text-jet-gray-600'}`}
                        value={values.business_name ? values.business_name.toUpperCase() : ''}
                        onChange={(e) => handleChange('business_name', e.target.value)}
                        readOnly={!isEditing}
                        placeholder="COMPANY NAME"
                      />
                    </div>
                  )}
                  <div className="form-field flex flex-col">
                    <input
                      type="email"
                      className={`profile-input w-full px-4 py-3 border rounded-md text-sm transition-colors duration-300 focus:outline-none ${isEditing ? 'bg-white text-jet-gray-800 focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]' : 'bg-gray-100 text-jet-gray-600'} ${errors.email && touched.email ? 'border-red-500' : 'border-jet-gray-300'}`}
                      value={values.email ? values.email : ''}
                      onChange={(e) => handleChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      readOnly={!isEditing}
                      placeholder="EMAIL ADDRESS"
                    />
                    {errors.email && touched.email && (
                      <span className="text-red-500 text-xs mt-1">{errors.email}</span>
                    )}
                  </div>
                  <div className="form-field phone-field flex flex-row gap-2">
                    <SearchableSelect
                      options={countries.map(country => ({
                        value: country.id,
                        label: `${country.name.toUpperCase()} ${country.phone_code}`
                      }))}
                      value={values.country_id}
                      onChange={(e) => handleChange('country_id', e.target.value)}
                      placeholder="SELECT COUNTRY"
                      disabled={!isEditing || loadingCountries}
                      className="w-1/2"
                      name="country_id"
                    />
                    <input
                      type="text"
                      className={`profile-input phone-input w-1/2 px-4 py-3 border rounded-md text-sm transition-colors duration-300 focus:outline-none ${isEditing ? 'bg-white text-jet-gray-800 focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]' : 'bg-gray-100 text-jet-gray-600'} ${errors.mobile_number && touched.mobile_number ? 'border-red-500' : 'border-jet-gray-300'
                        }`}
                      placeholder="MOBILE NUMBER"
                      value={values.mobile_number ? values.mobile_number.toUpperCase() : ''}
                      onChange={(e) => handleChange('mobile_number', e.target.value)}
                      onBlur={() => handleBlur('mobile_number')}
                      readOnly={!isEditing}
                    />
                    {errors.mobile_number && touched.mobile_number && (
                      <span className="text-red-500 text-xs mt-1 w-1/2">{errors.mobile_number}</span>
                    )}
                  </div>
                  <div className="form-field flex flex-row gap-2">
                    <SearchableSelect
                      options={nationalities.map(nationality => ({
                        value: nationality.id,
                        label: nationality.nationality ? nationality.nationality.toUpperCase() : ''
                      }))}
                      value={values.nationality}
                      onChange={(e) => handleChange('nationality', e.target.value)}
                      placeholder="SELECT NATIONALITY"
                      disabled={!isEditing || loadingNationalities}
                      className="w-1/2"
                      name="nationality"
                    />
                    <div className="w-1/2 flex flex-col">
                      <input
                        type="date"
                        className={`profile-input w-full px-4 py-3 border rounded-md text-sm transition-colors duration-300 focus:outline-none ${isEditing ? 'bg-white text-jet-gray-800 focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]' : 'bg-gray-100 text-jet-gray-600'} ${errors.date_of_birth && touched.date_of_birth ? 'border-red-500' : 'border-jet-gray-300'} [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:opacity-0`}
                        placeholder="DATE OF BIRTH"
                        value={values.date_of_birth}
                        max={new Date().toISOString().split('T')[0]}
                        onChange={(e) => handleChange('date_of_birth', e.target.value)}
                        onBlur={() => handleBlur('date_of_birth')}
                        onFocus={(e) => {
                          if (isEditing && e.target.showPicker) {
                            e.target.showPicker();
                          }
                        }}
                        readOnly={!isEditing}
                      />
                      {errors.date_of_birth && touched.date_of_birth && (
                        <span className="text-red-500 text-xs mt-1">{errors.date_of_birth}</span>
                      )}
                    </div>
                  </div>
                  <div className="form-field flex flex-col">
                    <textarea
                      className={`profile-textarea w-full px-4 py-3 border border-jet-gray-300 rounded-md text-sm transition-colors duration-300 resize-y min-h-11 focus:outline-none ${isEditing ? 'bg-white text-jet-gray-800 focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]' : 'bg-gray-100 text-jet-gray-600'}`}
                      placeholder="ADDRESS"
                      rows="2"
                      value={values.address ? values.address.toUpperCase() : ''}
                      onChange={(e) => handleChange('address', e.target.value)}
                      readOnly={!isEditing}
                    ></textarea>
                  </div>
                </div>
                {/* Button Container */}
                <div className="button-container flex justify-between items-center mt-4 mb-2">
                  {isEditing ? (
                    <button
                      onClick={handleCancel}
                      disabled={profileLoading}
                      className="text-gray-600 text-sm hover:text-gray-800 transition-colors duration-200 bg-none border-none cursor-pointer p-0 underline disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  ) : (
                    <button
                      onClick={handleEditClick}
                      className="text-gray-600 flex text-sm hover:text-gray-800 transition-colors duration-200 bg-none border-none cursor-pointer p-0 underline"
                    >
                      Edit
                    </button>
                  )}

                  <button
                    onClick={handleSave}
                    disabled={profileLoading}
                    className="save-button bg-jet-yellow text-jet-gray-800 border-0 px-6 sm:px-8 py-3 rounded-md text-sm font-semibold cursor-pointer transition-all duration-300 hover:bg-jet-yellow-hover hover:-translate-y-0.5 focus:outline-none focus:shadow-[0_0_0_2px_rgba(255,215,0,0.4)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-jet-yellow"
                  >
                    {profileLoading ? 'SAVING...' : 'SAVE'}
                  </button>
                </div>

                {/* Deactivate Account Button */}
                <div className="deactivate-container mt-6 pt-6 border-t border-jet-gray-200">
                  <button
                    onClick={handleDeactivateAccount}
                    disabled={deactivating}
                    className="w-full bg-red-500 text-white border-0 px-6 py-3 rounded-md text-sm font-semibold cursor-pointer transition-all duration-300 hover:bg-red-600 hover:-translate-y-0.5 focus:outline-none focus:shadow-[0_0_0_2px_rgba(239,68,68,0.4)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-red-500"
                  >
                    {deactivating ? 'DEACTIVATING...' : 'DEACTIVATE ACCOUNT'}
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Deactivating your account will require admin approval to reactivate
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <UniversalConfirmModal
        isOpen={showEditModal}
        onClose={handleCancelEdit}
        onConfirm={handleConfirmEdit}
        type="warning"
        title="Are you sure?"
        message="Are you sure. You want to edit profile"
        confirmText="YES"
        cancelText="CANCEL"
        theme="tailwind"
      />

      {/* Disable 2FA Confirmation Modal */}
      <UniversalConfirmModal
        isOpen={showDisable2FAModal}
        onClose={handleCancelDisable2FA}
        onConfirm={handleConfirmDisable2FA}
        type="warning"
        title="Disable 2FA?"
        message="Are you sure you want to disable two-factor authentication? This will make your account less secure."
        confirmText="YES, DISABLE"
        cancelText="CANCEL"
        theme="tailwind"
      />

      {/* Deactivate Account Confirmation Modal */}
      <UniversalConfirmModal
        isOpen={showDeactivateModal}
        onClose={handleCancelDeactivate}
        onConfirm={handleConfirmDeactivate}
        type="warning"
        title="Deactivate Account?"
        message="Are you sure you want to deactivate your account? You will need to contact the admin to reactivate it."
        confirmText="YES, DEACTIVATE"
        cancelText="CANCEL"
        theme="tailwind"
      />

      {/* Two Factor Authentication Modal */}
      <TwoFactorAuthModal
        isOpen={showTwoFactorModal}
        onClose={handleTwoFactorClose}
        onSave={handleTwoFactorSave}
      />
    </div>
  );
};

export default UserDetailsPage;